import React, { useEffect, useState, useCallback } from 'react';
import { getApiUrl } from '../../config/api';

interface AlertConfig {
  id: string;
  asset_symbol: string;
  condition: string;
  threshold: number | null;
  channels: string[];
  enabled: boolean;
  cooldown_minutes: number;
  last_fired_at: string | null;
}

const CONDITION_LABELS: Record<string, string> = {
  score_above: 'Score Above',
  score_below: 'Score Below',
  score_cross_zero: 'Regime Change (cross zero)',
  whale_inflow: 'Whale CEX Inflow >',
  whale_outflow: 'Whale CEX Outflow >',
};

const PressureAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [condition, setCondition] = useState('score_cross_zero');
  const [threshold, setThreshold] = useState('30');
  const [cooldown, setCooldown] = useState('30');
  const [creating, setCreating] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const resp = await fetch(getApiUrl('/api/pressure/alerts'), { credentials: 'include' });
      if (resp.ok) setAlerts(await resp.json());
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const createAlert = async () => {
    setCreating(true);
    try {
      const resp = await fetch(getApiUrl('/api/pressure/alerts'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          asset_symbol: 'TON',
          condition,
          threshold: condition === 'score_cross_zero' ? null : parseFloat(threshold),
          channels: ['telegram'],
          cooldown_minutes: parseInt(cooldown),
        }),
      });
      if (resp.ok) await fetchAlerts();
    } catch (err) {
      console.error('Failed to create alert:', err);
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (id: string) => {
    try {
      await fetch(getApiUrl(`/api/pressure/alerts/${id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      setAlerts(alerts.filter(a => a.id !== id));
    } catch (err) {
      console.error('Failed to delete alert:', err);
    }
  };

  const needsThreshold = condition !== 'score_cross_zero';

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-viz-text-primary">Pressure Alerts</h1>
        <p className="text-viz-text-secondary mt-1">
          Get notified when TON pressure score crosses key thresholds
        </p>
      </div>

      {/* Create Alert */}
      <div className="glass-card p-6 mb-6">
        <h2 className="text-lg font-semibold text-viz-text-primary mb-4">New Alert</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-viz-text-secondary block mb-1">Condition</label>
            <select
              value={condition}
              onChange={e => setCondition(e.target.value)}
              className="w-full bg-viz-bg-tertiary text-viz-text-primary border border-viz-border rounded px-3 py-2 text-sm"
            >
              {Object.entries(CONDITION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          {needsThreshold && (
            <div>
              <label className="text-xs text-viz-text-secondary block mb-1">
                Threshold {condition.includes('whale') ? '($)' : '(score)'}
              </label>
              <input
                type="number"
                value={threshold}
                onChange={e => setThreshold(e.target.value)}
                className="w-full bg-viz-bg-tertiary text-viz-text-primary border border-viz-border rounded px-3 py-2 text-sm"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-viz-text-secondary block mb-1">Cooldown (min)</label>
            <input
              type="number"
              value={cooldown}
              onChange={e => setCooldown(e.target.value)}
              className="w-full bg-viz-bg-tertiary text-viz-text-primary border border-viz-border rounded px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={createAlert}
              disabled={creating}
              className="w-full bg-viz-primary text-white rounded px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Alert'}
            </button>
          </div>
        </div>
      </div>

      {/* Alert List */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-viz-text-primary mb-4">
          Active Alerts ({alerts.length})
        </h2>
        {loading ? (
          <div className="text-viz-text-secondary text-sm">Loading...</div>
        ) : alerts.length === 0 ? (
          <div className="text-viz-text-secondary text-sm">
            No alerts configured. Create one above to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map(alert => (
              <div key={alert.id} className="flex items-center justify-between bg-viz-bg-tertiary rounded-lg p-4">
                <div>
                  <div className="text-sm text-viz-text-primary font-medium">
                    {CONDITION_LABELS[alert.condition] || alert.condition}
                    {alert.threshold != null && (
                      <span className="font-mono ml-1">
                        {alert.condition.includes('whale') ? `$${alert.threshold.toLocaleString()}` : alert.threshold}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-viz-text-secondary mt-1">
                    {alert.asset_symbol} · Cooldown: {alert.cooldown_minutes}min ·
                    Channel: {alert.channels?.join(', ')}
                    {alert.last_fired_at && (
                      <span> · Last fired: {new Date(alert.last_fired_at).toLocaleString()}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => deleteAlert(alert.id)}
                  className="text-red-400 hover:text-red-300 text-sm px-3 py-1"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PressureAlerts;
